import { useGamificationDB } from '@/hooks/useGamificationDB';
import { BadgeDisplay } from '@/components/gamification/BadgeDisplay';
import { StreakTracker } from '@/components/gamification/StreakTracker';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress as ProgressBar } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Trophy, BookOpen, Award, ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import progressMountainPath from '@/assets/progress-mountain-path.jpg';

export default function Progress() {
  const navigate = useNavigate();
  const gamification = useGamificationDB();

  const badgeToPathMap: Record<string, string> = {
    'hrt-champion': 'hrt',
    'sleep-specialist': 'sleep',
    'nutrition-ninja': 'nutrition',
    'heart-hero': 'heart'
  };

  const handleBadgeClick = (badgeId: string) => {
    const pathId = badgeToPathMap[badgeId];
    if (pathId) {
      navigate('/learning', { state: { selectedPathId: pathId } });
    }
  };

  const unlockedBadges = gamification.badges.filter(b => b.unlocked);
  const totalLessons = gamification.progress.reduce((sum, p) => sum + p.completedLessons.length, 0);
  const completionRate = gamification.progress.length > 0
    ? Math.round((gamification.progress.reduce((sum, p) => sum + (p.completedLessons.length / p.totalLessons), 0) / gamification.progress.length) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-background relative">
      {/* Background Image with Fade */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url(${progressMountainPath})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/90 to-background/95" />
      </div>

      {/* Content */}
      <div className="relative z-10">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
          <h1 className="text-4xl font-bold mb-2">Your Progress Dashboard</h1>
          <p className="text-muted-foreground">Track your learning journey and celebrate your achievements</p>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                <Trophy className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-3xl font-bold">{gamification.totalPoints}</p>
                <p className="text-sm text-muted-foreground">Total Points</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-secondary/10 to-secondary/5">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <p className="text-3xl font-bold">{totalLessons}</p>
                <p className="text-sm text-muted-foreground">Lessons Completed</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-accent/10 to-accent/5">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                <Award className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="text-3xl font-bold">{unlockedBadges.length}/{gamification.badges.length}</p>
                <p className="text-sm text-muted-foreground">Badges Earned</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Streak Tracker */}
        <div className="mb-8">
          <StreakTracker streak={gamification.streak} />
        </div>

        {/* Learning Paths Progress */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Learning Paths Progress</CardTitle>
            <CardDescription>Your completion status across all learning paths</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {gamification.progress.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Start your first lesson to begin tracking progress! 🚀
              </p>
            ) : (
              gamification.progress.map(path => {
                const percentage = Math.round((path.completedLessons.length / path.totalLessons) * 100);
                return (
                  <div key={path.pathId} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{path.pathName}</span>
                      <span className="text-sm text-muted-foreground">
                        {path.completedLessons.length}/{path.totalLessons} lessons
                      </span>
                    </div>
                    <ProgressBar value={percentage} className="h-2" />
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Badges Section */}
        <Card>
          <CardHeader>
            <CardTitle>Badge Collection</CardTitle>
            <CardDescription>
              Unlock badges by completing learning paths, maintaining streaks, and acing quizzes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {gamification.badges.map(badge => (
                <BadgeDisplay 
                  key={badge.id} 
                  badge={badge} 
                  size="sm"
                  onClick={badge.category === 'completion' ? () => handleBadgeClick(badge.id) : undefined}
                />
              ))}
            </div>
            <p className="text-sm text-muted-foreground text-center mt-4">
              💡 Click on unlocked learning path badges to start or continue that path
            </p>
          </CardContent>
        </Card>

        {/* Quiz Scores */}
        {gamification.quizScores.length > 0 && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Quiz History</CardTitle>
              <CardDescription>Your recent quiz performance</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {gamification.quizScores.slice(-5).reverse().map((quiz, idx) => (
                  <div key={idx} className="flex justify-between items-center p-3 rounded-lg bg-secondary/20">
                    <div>
                      <p className="font-medium">{quiz.quizName}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(quiz.completedAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold">
                        {quiz.score}/{quiz.totalQuestions}
                      </p>
                      <p className="text-xs text-muted-foreground">
                      {Math.round((quiz.score / quiz.totalQuestions) * 100)}%
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      </div>
    </div>
  );
}
